import { doZonesOverlap, isPointInZone, isTableLabel, isZoneInSelection } from "../../helpers";
import { CELL_THRESHOLD } from "../../Renderer/TableCellZoning/state/types";
import { Point, Size } from "../../Renderer/types";
import { Zone, ZoneDimensions, ZoneWithCells } from "../../types";
import { CurrentAction, PageSaveState, PageState, State } from "../state";

export interface MouseMoveAction {
  imageSize: Size;
  location: Point;
  shift: boolean;
  type: "MOUSE MOVE";
}

const creatingOrSelectingZones = (state: PageState, action: MouseMoveAction): PageState => {
  if (!state.selection) {
    if (state.firstDragEvent) {
      return { ...state, firstDragEvent: false };
    }
    return state;
  }
  const newSelection = {
    ...state.selection,
    endx: action.location.x,
    endy: action.location.y,
  };
  const intersectingZones: Array<number> = [];
  for (let i = 0; i < state.zones.length; i++) {
    if (isZoneInSelection(newSelection)(state.zones[i])) {
      intersectingZones.push(i);
    }
  }
  return {
    ...state,
    activeZones: intersectingZones,
    currentAction:
      intersectingZones.length > 0 ? CurrentAction.SELECTING : CurrentAction.CREATING_ZONE,
    firstDragEvent: false,
    previousMouseLocation: action.location,
    selection: newSelection,
  };
};

const filterValidCells = (c: ZoneDimensions | undefined): c is ZoneDimensions => !!c;
export const updateTableCells = (
  cells: ReadonlyArray<ZoneDimensions>,
  zone: ZoneWithCells,
  threshold: number,
): ReadonlyArray<ZoneDimensions> => {

  const newCells = cells.map((c: ZoneDimensions): ZoneDimensions | undefined => {
    if (c.maxX < zone.minX || c.maxY < zone.minY || c.minX > zone.maxX || c.minY > zone.maxY) {
      return undefined;
    }

    const toReturn: ZoneDimensions = {
      ...c,
      maxX: Math.min(zone.maxX, c.maxX),
      maxY: Math.min(zone.maxY, c.maxY),
      minX: Math.max(zone.minX, c.minX),
      minY: Math.max(zone.minY, c.minY),
    };
    if (toReturn.maxX - toReturn.minX < threshold || toReturn.maxY - toReturn.minY < threshold) {
      return undefined;
    }
    return toReturn;
  });
  
  const filteredCells: Array<ZoneDimensions> = newCells.filter(filterValidCells);
  if (filteredCells.length > 1) {
    return filteredCells;
  }
  return [
    {
      ...cells[0],
      maxX: zone.maxX,
      maxY: zone.maxY,
      minX: zone.minX,
      minY: zone.minY,
    },
  ];
};

const movingZonesByMouse = (
  state: PageState,
  action: MouseMoveAction,
  taxonomy: State["taxonomy"],
  cellThreshold: number,
): PageState => {
  if (state.previousMouseLocation && state.activeZones.length > 0) {
    let zones = movingZones(
      state, 
      action.location.x - state.previousMouseLocation.x,
      action.location.y - state.previousMouseLocation.y,
      action.imageSize,
      taxonomy,
      cellThreshold
    );
    if (zones !== state.zones) {
      return {
        ...state,
        firstDragEvent: false,
        pageSaveState: PageSaveState.Dirty,
        previousMouseLocation: action.location,
        zones: state.firstDragEvent
          ? state.zoneHistory.push(zones as Zone[])
          : state.zoneHistory.replace(zones as Zone[]),
      };
    }
  }

  return {
    ...state,
    firstDragEvent: false,
    previousMouseLocation: action.location,
  };
};

export const movingZones = (
  state: PageState,
  deltaX : number, 
  deltaY: number,
  size: Size,
  taxonomy: State["taxonomy"],
  cellThreshold: number,
  allowResizingNeighbors : boolean = false
): Zone[] | ReadonlyArray<Zone> => {
    const diff = {
      x: deltaX,
      y: deltaY,
    };
    const staticZones: Array<Zone> = [];
    const updatedZones = state.zones.map((zone, index) => {
      if (state.activeZones.indexOf(index) < 0) {
        staticZones.push(zone);
        return zone;
      }
      let updatedZone = {
        ...zone,
        maxX: zone.maxX + diff.x,
        maxY: zone.maxY + diff.y,
        minX: zone.minX + diff.x,
        minY: zone.minY + diff.y,
      };
      if (isTableLabel(zone.label, taxonomy) && Array.isArray(zone.cells)) {
        updatedZone = {
          ...updatedZone,
          cells: updateTableCells(zone.cells, updatedZone as ZoneWithCells, cellThreshold),
        };
      }
      return updatedZone;
    });
    for (const activeIndex of state.activeZones) {
      const activeZone = updatedZones[activeIndex];
      if (
        activeZone.minX < 0 ||
        activeZone.minY < 0 ||
        activeZone.maxX > size.width ||
        activeZone.maxY > size.height
      ) {
        return state.zones;
      }
      const intersection = !!staticZones.find(doZonesOverlap(activeZone));
      if (intersection) {
        return state.zones;
      }
    }
    return updatedZones;
};

const hoveringNothing = (state: PageState, action: MouseMoveAction): PageState => {
  const intersectingZoneIndex = state.zones.findIndex(isPointInZone(action.location));
  if (intersectingZoneIndex >= 0) {
    return {
      ...state,
      currentAction: CurrentAction.HOVERING_ZONE,
      firstDragEvent: false,
      hoveredZone: intersectingZoneIndex,
    };
  }
  return state;
};

const hoveringZone = (state: PageState, action: MouseMoveAction): PageState => {
  const intersectingZoneIndex = state.zones.findIndex(isPointInZone(action.location));
  if (intersectingZoneIndex < 0) {
    return {
      ...state,
      currentAction: CurrentAction.HOVERING_NOTHING,
      firstDragEvent: false,
      hoveredZone: -1,
    };
  } else if (intersectingZoneIndex !== state.hoveredZone) {
    return {
      ...state,
      firstDragEvent: false,
      hoveredZone: intersectingZoneIndex,
    };
  }
  return state;
};

const resizingBottomByMouse = (
  state: PageState,
  action: MouseMoveAction,
  taxonomy: State["taxonomy"],
  threshold: number,
): PageState => {

  if (!state.previousMouseLocation) {
    return state;
  }
  let zones = resizingBottom(
    state,
    state.previousMouseLocation.y - action.location.y,
    taxonomy,
    action.imageSize,
    threshold,
    action.shift
  );
  if (zones === state.zones) {
    return { ...state, previousMouseLocation: action.location };
  }

  return {
    ...state,
    firstDragEvent: false,
    pageSaveState: PageSaveState.Dirty,
    previousMouseLocation: action.location,
    zones: state.firstDragEvent
      ? state.zoneHistory.push(zones as Zone[])
      : state.zoneHistory.replace(zones as Zone[]),
  };

  
};

const resizingLeftByMouse = (
  state: PageState,
  action: MouseMoveAction,
  taxonomy: State["taxonomy"],
  threshold: number,
): PageState => {
  if (!state.previousMouseLocation) {
    return state;
  }
  let zones = resizingLeft(
    state,
    state.previousMouseLocation.x - action.location.x,
    taxonomy,
    threshold,
    action.shift
  );
  if (zones === state.zones) {
    return { ...state, previousMouseLocation: action.location };
  }

  return {
    ...state,
    firstDragEvent: false,
    pageSaveState: PageSaveState.Dirty,
    previousMouseLocation: action.location,
    zones: state.firstDragEvent
      ? state.zoneHistory.push(zones as Zone[])
      : state.zoneHistory.replace(zones as Zone[]),
  };
};

const resizingRightByMouse = (
  state: PageState,
  action: MouseMoveAction,
  taxonomy: State["taxonomy"],
  threshold: number,
): PageState => {
  if (!state.previousMouseLocation) {
    return state;
  }
  let zones = resizingRight(
    state,
    state.previousMouseLocation.x - action.location.x,
    taxonomy,
    action.imageSize,
    threshold,
    action.shift
  );
  if (zones === state.zones) {
    return { ...state, previousMouseLocation: action.location };
  }

  return {
    ...state,
    firstDragEvent: false,
    pageSaveState: PageSaveState.Dirty,
    previousMouseLocation: action.location,
    zones: state.firstDragEvent
      ? state.zoneHistory.push(zones as Zone[])
      : state.zoneHistory.replace(zones as Zone[]),
  };
};

const resizingTopByMouse = (
  state: PageState,
  action: MouseMoveAction,
  taxonomy: State["taxonomy"],
  threshold: number,
): PageState => {
  if (!state.previousMouseLocation) {
    return state;
  }
  let zones = resizingTop(
    state,
    state.previousMouseLocation.y - action.location.y,
    taxonomy,
    threshold,
    action.shift
  );
  if (zones === state.zones) {
    return { ...state, previousMouseLocation: action.location };
  }

  return {
    ...state,
    firstDragEvent: false,
    pageSaveState: PageSaveState.Dirty,
    previousMouseLocation: action.location,
    zones: state.firstDragEvent
      ? state.zoneHistory.push(zones as Zone[])
      : state.zoneHistory.replace(zones as Zone[]),
  };
};

export const resizingTop = (
  state: PageState,
  deltaY: number,
  taxonomy: State["taxonomy"],
  threshold: number,
  allowResizingNeighbors : boolean = false
): Zone[] | ReadonlyArray<Zone> => {

  const activeZone = state.zones[state.activeZones[0]];
  const previousHeight = activeZone.maxY - activeZone.minY;
  let updatedZone = {
    ...activeZone,
    minY: Math.max(0, activeZone.minY - deltaY),
  };
  if (isTableLabel(activeZone.label, taxonomy) && Array.isArray(activeZone.cells)) {
    updatedZone = {
      ...updatedZone,
      cells: updateTableCells(activeZone.cells, updatedZone as ZoneWithCells, threshold),
    };
  }
  const newHeight = updatedZone.maxY - updatedZone.minY;
  if (newHeight < previousHeight && newHeight < 5) {
    return state.zones;
  }
  // check for intersection

  let newZones = [];
  const overlapFunction = doZonesOverlap(updatedZone);
  for (let i = 0; i < state.zones.length; i++) {
    if (i === state.activeZones[0]) {
      newZones.push(updatedZone);
      continue;
    }
    if (overlapFunction(state.zones[i])) {
      if (!allowResizingNeighbors) {
        return state.zones;
      } else {
        // Don't let the neighbor be reduced to nothing
        if (state.zones[i].maxY - state.zones[i].minY - deltaY < 3) {
          return state.zones;
        }

        newZones.push({
          ...state.zones[i],
          maxY: updatedZone.minY - 0.1
        }); 
      }
    } else {
      newZones.push(state.zones[i]);
    }
  }
  return newZones;
};

export const resizingBottom = (
  state: PageState,
  deltaY: number,
  taxonomy: State["taxonomy"],
  size: Size,
  threshold: number,
  allowResizingNeighbors : boolean = false
): Zone[] | ReadonlyArray<Zone> => {
  const activeZone = state.zones[state.activeZones[0]];
  const previousHeight = activeZone.maxY - activeZone.minY;
  let updatedZone = {
    ...activeZone,
    maxY: Math.min(
      size.height,
      activeZone.maxY - deltaY,
    )
  };
  if (isTableLabel(activeZone.label, taxonomy) && Array.isArray(activeZone.cells)) {
    updatedZone = {
      ...updatedZone,
      cells: updateTableCells(activeZone.cells, updatedZone as ZoneWithCells, threshold),
    };
  }
  const newHeight = updatedZone.maxY - updatedZone.minY;
  if (newHeight < previousHeight && newHeight < 5) {

    return state.zones;
  }

  let newZones = [];
  const overlapFunction = doZonesOverlap(updatedZone);
  for (let i = 0; i < state.zones.length; i++) {
    if (i === state.activeZones[0]) {
      newZones.push(updatedZone);
      continue;
    }

    if (overlapFunction(state.zones[i])) {

      if (!allowResizingNeighbors) {
        return state.zones;
      } else {
        
        // Don't let the neighbor be reduced to nothing
        if (state.zones[i].maxY - state.zones[i].minY + deltaY < 3) {
          return state.zones;
        }
        newZones.push({
          ...state.zones[i],
          minY: updatedZone.maxY + 0.1
        }); 
      }
    } else {
      newZones.push(state.zones[i]);
    }
  }
  return newZones;
};

export const resizingRight = (
  state: PageState,
  deltaX: number,
  taxonomy: State["taxonomy"],
  size: Size,
  threshold: number,
  allowResizingNeighbors : boolean = false
): Zone[] | ReadonlyArray<Zone> => {
  console.log(size.width);
  const activeZone = state.zones[state.activeZones[0]];
  console.log(activeZone.maxX);

  const previousWidth = activeZone.maxY - activeZone.minY;
  let updatedZone = {
    ...activeZone,
    maxX: Math.min(size.width, activeZone.maxX - deltaX),
  };
  if (isTableLabel(activeZone.label, taxonomy) && Array.isArray(activeZone.cells)) {
    updatedZone = {
      ...updatedZone,
      cells: updateTableCells(activeZone.cells, updatedZone as ZoneWithCells, threshold),
    };
  }
  const newWidth = updatedZone.maxX - updatedZone.minX;
  if (newWidth < previousWidth && newWidth < 5) { 
    return state.zones;
  }

  let newZones = [];
  const overlapFunction = doZonesOverlap(updatedZone);
  for (let i = 0; i < state.zones.length; i++) {
    if (i === state.activeZones[0]) {
      newZones.push(updatedZone);
      continue;
    }
    if (overlapFunction(state.zones[i])) {
      if (!allowResizingNeighbors) {
        return state.zones;
      } else {
        // Don't let the neighbor be reduced to nothing
        if (state.zones[i].maxX - state.zones[i].minX + deltaX < 5) {
          return state.zones;
        }
        newZones.push({
          ...state.zones[i],
          minX: updatedZone.maxX + 0.1
        }); 
      }
    } else {
      newZones.push(state.zones[i]);
    }
  }
  return newZones;
};

export const resizingLeft = (
  state: PageState,
  deltaX: number,
  taxonomy: State["taxonomy"],
  threshold: number,
  allowResizingNeighbors : boolean = false
): Zone[] | ReadonlyArray<Zone> => {

  const activeZone = state.zones[state.activeZones[0]];
  const previousWidth = activeZone.maxY - activeZone.minY;
  let updatedZone = {
    ...activeZone,
    minX: Math.max(0, activeZone.minX - deltaX),
  };
  if (isTableLabel(activeZone.label, taxonomy) && Array.isArray(activeZone.cells)) {
    updatedZone = {
      ...updatedZone,
      cells: updateTableCells(activeZone.cells, updatedZone as ZoneWithCells, threshold),
    };
  }
  const newWidth = updatedZone.maxX - updatedZone.minX;
  if (newWidth < previousWidth && newWidth < 5) { 
    return state.zones;
  }

  let newZones = [];
  const overlapFunction = doZonesOverlap(updatedZone);
  for (let i = 0; i < state.zones.length; i++) {
    if (i === state.activeZones[0]) {
      newZones.push(updatedZone);
      continue;
    }
    if (overlapFunction(state.zones[i])) {
      if (!allowResizingNeighbors) {
        return state.zones;
      } else {
        // Don't let the neighbor be reduced to nothing
        if (state.zones[i].maxX - state.zones[i].minX - deltaX < 3) {
          return state.zones;
        }
        newZones.push({
          ...state.zones[i],
          maxX: updatedZone.minX - 0.1
        }); 
      }
    } else {
      newZones.push(state.zones[i]);
    }
  }
  return newZones;
};


const mouseMovedOnPage = (
  state: PageState,
  action: MouseMoveAction,
  taxonomy: State["taxonomy"],
  cellThreshold: number,
): PageState => {
  switch (state.currentAction) {
    case CurrentAction.CREATING_ZONE:
    case CurrentAction.SELECTING:
      return creatingOrSelectingZones(state, action);
    case CurrentAction.MOVING:
      return movingZonesByMouse(state, action, taxonomy, cellThreshold);
    case CurrentAction.HOVERING_NOTHING:
      return hoveringNothing(state, action);
    case CurrentAction.HOVERING_ZONE:
      return hoveringZone(state, action);
    case CurrentAction.RESIZING_BOTTOM:
      return resizingBottomByMouse(state, action, taxonomy, cellThreshold);
    case CurrentAction.RESIZING_LEFT:
      return resizingLeftByMouse(state, action, taxonomy, cellThreshold);
    case CurrentAction.RESIZING_RIGHT:
      return resizingRightByMouse(state, action, taxonomy, cellThreshold);
    case CurrentAction.RESIZING_TOP:
      return resizingTopByMouse(state, action, taxonomy, cellThreshold);
  }
};

export const mouseMove = (state: State, action: MouseMoveAction): State => {
  const updatedPage = mouseMovedOnPage(
    state.pages[state.activePage],
    action,
    state.taxonomy,
    CELL_THRESHOLD,
  );
  if (updatedPage === state.pages[state.activePage]) {
    return {
        ...state,
        pointerPosition: action.location
    };
  }
  let currentLabel = state.currentLabel;
  // check if the active zones changed
  if (
    updatedPage.activeZones !== state.pages[state.activePage].activeZones &&
    updatedPage.activeZones.length > 0
  ) {
    const uniqueLabels: { [key: string]: true } = {};
    for (const zoneIndex of updatedPage.activeZones) {
      uniqueLabels[updatedPage.zones[zoneIndex].label] = true;
    }
    if (Object.keys(uniqueLabels).length === 1) {
      currentLabel = Object.keys(uniqueLabels)[0];
    }
  }

  return {
    ...state,
    currentLabel,
    pointerPosition: action.location,
    pages: [
      ...state.pages.slice(0, state.activePage),
      updatedPage,
      ...state.pages.slice(state.activePage + 1),
    ],
  };
};
